import { Link } from "react-router-dom";
import { CommonCalendarOptions, DateRange } from "../../types";
import { CalendarFilters } from "../../filters";
import { useCalendarEventsWithFilters } from "../../hooks";
import { Calendar, cn, useDateFnsFormattersWithLocale } from "@tellescope/ui";
import { CompactMonthDayButton } from "./CompactMonthDayButton";
import { TellescopeModelFromName } from "@tellescope/tanstack-query";
import { getEventsByDate } from "../../utils";
import { useMemo } from "react";

export interface CompactMonthViewProps extends DateRange, CommonCalendarOptions {
  className?: string;
  onHeaderClick: (() => void) | { to: string; replace?: boolean };
  getOnDateClick: (date: Date) => (() => void) | { to: string; replace?: boolean };
  events?: TellescopeModelFromName<"calendar_events">[];
  filters?: CalendarFilters;
  currentUserId?: string;
}

export const CompactMonthView = ({
  startDate,
  referenceDate = startDate,
  timezone = Intl.DateTimeFormat().resolvedOptions().timeZone,
  weekStart = "sunday",
  className,
  onHeaderClick,
  getOnDateClick,
  events,
  filters,
  currentUserId,
}: CompactMonthViewProps) => {
  const { format } = useDateFnsFormattersWithLocale();
  const HeaderComponent =
    typeof onHeaderClick === "function" ? "button" : typeof onHeaderClick === "object" ? Link : "div";

  const eventsQuery = useCalendarEventsWithFilters({
    referenceDate,
    view: "month",
    timezone,
    weekStart,
    enabled: events === undefined,
    filters,
    currentUserId,
  });

  const eventsByDate = useMemo(
    () => getEventsByDate(events ?? eventsQuery.data ?? [], timezone),
    [events, eventsQuery.data, timezone]
  );

  return (
    <div className={cn("flex flex-col rounded-md border overflow-hidden", className)}>
      <HeaderComponent
        className={cn(
          "border-b flex items-center justify-center p-2 text-sm",
          HeaderComponent !== "div" && "transition-colors hover:bg-gray-100"
        )}
        to={
          typeof HeaderComponent === "object" && onHeaderClick && typeof onHeaderClick === "object"
            ? onHeaderClick.to
            : ""
        }
        replace={
          typeof HeaderComponent === "object" && onHeaderClick && typeof onHeaderClick === "object"
            ? onHeaderClick.replace
            : undefined
        }
        onClick={HeaderComponent === "button" && typeof onHeaderClick === "function" ? onHeaderClick : undefined}
      >
        {format(startDate, "MMMM")}
      </HeaderComponent>
      <Calendar
        month={startDate}
        showOutsideDays={false}
        hideNavigation
        components={{
          Day: ({ day }) => (
            <CompactMonthDayButton
              date={day.date}
              events={eventsByDate[day.date.getTime()] ?? []}
              onClick={getOnDateClick?.(day.date)}
              referenceDate={startDate}
            />
          ),
          MonthCaption: () => <></>,
          Week: ({ children }) => <div className="w-full grid grid-cols-7 my-1.5">{children}</div>,
        }}
        className="font-light w-full"
      />
    </div>
  );
};
